/**
 * THE FIREBASE WEB CONFIG, REQUIRED RATHER THAN GUESSED.
 *
 * Owner 2026-09-13: *"don't fall back like this, prod and staging are diff, better to find out what is
 * missing earlier"*. Right, and the failure it prevents is the nastiest kind. `authDomain`, `projectId` and
 * `storageBucket` used to fall back to hardcoded PRODUCTION values, so a build that lost its environment did
 * not break — it came up pointing at production Firebase while claiming to be staging. Nothing in the page
 * would look wrong. The first symptom would be staging traffic writing production data.
 *
 * A default is only safe when every environment wants the same answer. These three are precisely the values
 * that MUST differ between environments, so a default here is not a convenience, it is a wrong answer waiting
 * to be believed. Same reasoning as the server rule that source reads `process.env.X` with no string-literal
 * fallback (CLAUDE.md Security 13).
 *
 * WHERE THIS IS CAUGHT, earliest first:
 *   1. The DEPLOY WORKFLOW verifies every value before it builds, so a missing one never becomes a bundle.
 *   2. This throws at boot, naming every variable that is absent, so a bundle built any other way says so
 *      immediately and precisely instead of behaving like a different environment.
 *
 * None of these are secrets. They are public build-time config baked into the bundle: the secret-shaped ones
 * come from Secret Manager per project, the environment-shaped ones from the deploy workflow's config job.
 */

/** Every value the web app cannot start without. `measurementId` is absent on purpose: analytics is optional. */
export const REQUIRED_WEB_CONFIG = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
] as const;

export interface WebFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * Read the config, or throw naming EVERY missing variable at once.
 *
 * All of them, not the first: someone fixing a broken build should learn the whole list in one pass rather
 * than rediscovering it one deploy at a time.
 */
export function readWebConfig(env: Record<string, string | undefined>): WebFirebaseConfig {
  const missing = REQUIRED_WEB_CONFIG.filter((key) => !String(env[key] || '').trim());
  if (missing.length) {
    throw new Error(
      `Firebase web config is incomplete: ${missing.join(', ')}. `
      + 'These differ per environment and are never defaulted, because a default would silently point this '
      + 'build at another project. Locally, copy them into .env.local (firebase apps:sdkconfig WEB --project '
      + '<your project>); in CI they come from the deploy workflow.',
    );
  }
  return {
    apiKey: String(env.REACT_APP_FIREBASE_API_KEY),
    authDomain: String(env.REACT_APP_FIREBASE_AUTH_DOMAIN),
    projectId: String(env.REACT_APP_FIREBASE_PROJECT_ID),
    storageBucket: String(env.REACT_APP_FIREBASE_STORAGE_BUCKET),
    messagingSenderId: String(env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
    appId: String(env.REACT_APP_FIREBASE_APP_ID),
    // Optional, and only ever used to turn analytics on — absent simply means no analytics.
    ...(env.REACT_APP_FIREBASE_MEASUREMENT_ID ? { measurementId: String(env.REACT_APP_FIREBASE_MEASUREMENT_ID) } : {}),
  };
}
